import { useState, useEffect, useRef, createRef, ReactElement } from 'react'
import { useRouter } from 'next/router'
import ValidatorWrapper, { ValidatorField } from '@coxy/react-validator'
import cn from 'classnames'

import validatorRules from 'libs/validator-rules'
import { addSpacesInNumber, toFixed } from 'utils/formatters'

import * as ga from 'libs/ga'

import { useAppDispatch, useAppSelector } from 'store/hooks'
import { getCurrencies, setActiveCodeFrom } from 'store/reducers/currencies'
import { checkLiquidity, createLoan, estimateLoanAmount } from 'store/fetchers/loans'
import { ICurrency, ICurrencyWithBalanceAndRate } from 'store/fetchers/currencies'
import { EExchangeDirections, IExchangeData } from 'store/fetchers/loans/enum'
import { fetchSettings, getSettings } from 'store/reducers/settings'

import ButtonBlue from 'components/ui/button/_color/_blue'

import BackIcon from 'images/arrow-back.svg'

import { useTranslation } from 'utils/use-translation'

import { createApplicant, getKYCLink } from 'store/fetchers/users'

import BigNumber from 'bignumber.js'

import { goToChat } from 'utils/go-to-chat'

import ContentHeader from '../content/__header'

import { fetchWallets, selectedWallets, setActiveWallet } from '../../../../store/reducers/wallets'

import { roundNumberWithPrecision } from '../../../../utils/round-number'

import { floorBigNumber, floorNumber } from '../../../../utils/floor-number'

import PercentSlider from '../../percent-slider'

import mapCurrenciesAndWallets from '../../../../utils/map-currencies-and-wallets'

import { selectedUser } from '../../../../store/reducers/users'

import { countDecimalPlaces } from '../../../../utils/count-decimal-places'

import Skeleton from '../../skeleton'

import { safeMin } from '../../../../utils/safe-min'

import { safeMax } from '../../../../utils/safe-max'

import styles from './styles.module.scss'
import { LoanCalculatorProps } from './loan-calculator.props'
import InputCalculator from './components/input-calculator'
import KYCPopup from './components/kyc-popup'

import TermsBlock from './components/terms-block'

const DEFAULT_FROM = '0.1'
const buildAprOptions = (response: any, t: (key: string) => string, isDynamicLoansAvailable?: boolean) => {
  const options = [
    {
      id: 'popular',
      title: 'long_term',
      apr: 'fixed',
      percent: response?.fixed_apr_unlimited_loan ?? '',
      duration: 'unlimited_time',
      isDynamic: false,
      period: 'unlimited',
    },
    {
      id: 'short_term',
      title: 'short_term',
      apr: 'fixed',
      percent: response?.fixed_apr_fixed_loan ?? '',
      duration: 30 + ' ' + t('days'),
      isDynamic: false,
      period: '30',
    },
    ...(isDynamicLoansAvailable
      ? [
          {
            id: 'min_rate',
            title: 'min_rate',
            apr: 'dynamic',
            percent: response?.dynamic_apr_unlimited_loan ?? '',
            duration: 'unlimited_time',
            isDynamic: true,
            period: 'unlimited',
          },
        ]
      : []),
  ]

  return options.filter((opt) => Number(opt.percent) > 0)
}

const Calculator = ({ pageCurrency, isCurrenciesUpdated }: LoanCalculatorProps): ReactElement => {
  const { t, locale, replacePlaceholders, replacePlaceholdersWithElements } = useTranslation()

  const dispatch = useAppDispatch()

  const router = useRouter()
  const user = useAppSelector(selectedUser)

  const currencies = useAppSelector(getCurrencies)
  const settings = useAppSelector(getSettings)
  const wallets = useAppSelector(selectedWallets)

  const timer = useRef<ReturnType<typeof setTimeout>>()
  const validator = createRef<ValidatorWrapper>()
  const requestedPairRef = useRef<[ICurrency, ICurrency]>()
  const isMounted = useRef(true)
  const skipToEstimate = useRef(false)

  const [from, setFrom] = useState<ICurrency>(null)
  const [to, setTo] = useState<ICurrency>(null)
  const [amountTo, setAmountTo] = useState('')
  const [exchangeData, setExchangeData] = useState<IExchangeData>(null)
  const [isExternalWallet, setIsExternalWallet] = useState<boolean>(false)
  const [defaultFrom, setDefaultFrom] = useState<number>(0)
  const [amountPercent, setAmountPercent] = useState<number>(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingFrom, setLoadingFrom] = useState(false)
  const [isLoadingTo, setLoadingTo] = useState(true)
  const [listDepositCurrencies, setListDepositCurrencies] = useState<ICurrencyWithBalanceAndRate[]>([])
  const [listLoanCurrencies, setListLoanCurrencies] = useState<ICurrencyWithBalanceAndRate[]>([])
  const [estimateError, setEstimateError] = useState('')
  const [isNotEnoughBalance, setisNotEnoughBalance] = useState<boolean>(false)
  const [ltvPercent, setLtvPercent] = useState('')
  const [isInitialMount, setIsInitialMount] = useState<boolean>(true)
  const [isBtnActive, setIsBtnActive] = useState<boolean>(true)
  const [isDefaultFromLoaded, setIsDefaultFromLoaded] = useState<boolean>(false)
  const [walletToBalance, setWalletToBalance] = useState<number>(0)
  const [isKYCPopupActive, setIsKYCPopupActive] = useState<boolean>(false)
  const [isLiquidityError, setIsLiquidityError] = useState<boolean>(false)
  const estimateRoundDirection = useRef<'down' | 'up'>('up')

  const [filteredDepositCurrencies, setFilteredDepositCurrencies] = useState<ICurrencyWithBalanceAndRate[]>([])
  const [filteredLoanCurencies, setFilteredLoanCurrencies] = useState<ICurrency[]>([])

  const [allDepositCurrencies, setAllDepositCurrencies] = useState<ICurrencyWithBalanceAndRate[]>([])
  const [allLoanCurencies, setAllLoanCurrencies] = useState<ICurrency[]>([])

  const [isFirstVisited, setIsFirstVisited] = useState<boolean>(true)
  const [isWalletsReceived, setIsWalletsReceived] = useState<boolean>(false)
  const [isFirstChanged, setIsFirstChanged] = useState<boolean>(false)
  const [isCurrenciesFetched, setIsCurrenciesFetched] = useState<boolean>(false)

  const [ltvOptions, setLvtOptions] = useState<string[]>()
  const [aprOptions, setAprOptions] = useState(buildAprOptions(null, t, false))
  const [activeAprOption, setActiveAprOption] = useState('popular')
  const [isLiquidityLoaded, setIsLiquidityLoaded] = useState<boolean>(false)

  const initialFromCode = (router.query.fromCode as string) ?? ''
  const initialFromNetwork = (router.query.fromNetwork as string) ?? ''
  const initialToCode = (router.query.toCode as string) ?? ''
  const initialToNetwork = (router.query.toNetwork as string) ?? ''

  const [maxLoanAmount, setMaxLoanAmount] = useState('')
  const [maxLoanAmountWithoutLtv, setMaxLoanAmountWithoutLtv] = useState('')
  const [maxDepositAmountWithoutLtv, setMaxDepositAmountWithoutLtv] = useState('')

  const [actualMaxAmount, setActualMaxAmount] = useState<string>('')
  const [actualMinAmount, setActualMinAmount] = useState<string>('')

  const [_amountFrom, _setAmountFrom] = useState(DEFAULT_FROM)
  const amountChange = useRef<boolean>(false)

  const amountFrom = useRef(_amountFrom)
  const isAprOptionsSet = useRef(false)

  const [percentCoefficient] = useState<number>(2)

  const [createError, setCreateError] = useState('')

  const setAmountFrom = (value: string) => {
    const savedAmount = sessionStorage.getItem('loanCalculator:amount')

    if (!amountChange.current && savedAmount) {
      _setAmountFrom(defaultFrom !== 0 ? Math.min(+savedAmount, defaultFrom).toString() : savedAmount)
    } else {
      _setAmountFrom(value)
      amountFrom.current = value
    }

    if (isFirstVisited) {
      _setAmountFrom(savedAmount ?? value)

      amountFrom.current = savedAmount ?? value
    }
  }

  const handleSetFrom = (from: ICurrency) => {
    setIsFirstVisited(true)

    sessionStorage.setItem('loanCalculator:fromCurrencyCode', from.code)
    sessionStorage.setItem('loanCalculator:fromCurrencyNetwork', from.network)

    setIsFirstChanged(true)

    setFrom(from)
  }

  const handleSetTo = (to: ICurrency) => {
    sessionStorage.setItem('loanCalculator:toCurrencyCode', to.code)
    sessionStorage.setItem('loanCalculator:toCurrencyNetwork', to.network)
    setIsFirstChanged(true)
    setTo(to)
  }

  const onWalletSwitch = (isExternalWallet: boolean) => {
    sessionStorage.setItem('loanCalculator:isExternal', isExternalWallet ? '1' : '0')
    setIsFirstChanged(true)
    setIsExternalWallet(isExternalWallet)
  }

  const verifyKYC = async () => {
    try {
      await createApplicant()
      const kycLink = await getKYCLink()

      if (kycLink) {
        window.open(kycLink, '_blank', 'noopener,noreferrer')
      }
    } catch (error) {
      console.error(error)
    }
  }

  const getCurrency = (code?: string, network?: string) => {
    return currencies?.find(
      (item) =>
        item.code.toLowerCase() === code?.toLowerCase() && item.network.toLowerCase() === network?.toLowerCase(),
    )
  }

  const applyInitialCurrencies = () => {
    if (initialFromCode && initialFromNetwork && initialToCode && initialToNetwork) {
      const from = getCurrency(initialFromCode, initialFromNetwork)
      const to = getCurrency(initialToCode, initialToNetwork)

      if (from) {
        setFrom(from)
      }

      if (to) {
        setTo(to)
      }
    }
  }

  const fetchLiquitity = async () => {
    if (!from || !to) return
    setIsLiquidityLoaded(false)

    try {
      const res = await checkLiquidity({
        from_code: from?.code,
        from_network: from?.network,
        to_code: to?.code,
        to_network: to?.network,
      })

      if (res?.max_loan_amount || res?.max_deposit_amount) {
        setMaxLoanAmountWithoutLtv(res?.max_loan_amount)
        setMaxDepositAmountWithoutLtv(res?.max_deposit_amount)
        setIsLiquidityError(false)
      } else {
        setMaxLoanAmountWithoutLtv(null)
        setMaxDepositAmountWithoutLtv(null)
        setIsLiquidityError(true)
      }
    } catch (err) {
      console.error('Error trying get liquidity: ', err?.message)
      setMaxLoanAmountWithoutLtv(null)
      setMaxDepositAmountWithoutLtv(null)
      setIsLiquidityError(true)
    } finally {
      setIsLiquidityLoaded(true)
    }
  }

  useEffect(() => {
    fetchLiquitity()
  }, [from, to])

  const handleChangeAprOption = (id: string) => {
    setActiveAprOption(id)
  }

  useEffect(() => {
    if (!from || !to || !_amountFrom || isLoadingFrom) return

    if (skipToEstimate.current) {
      skipToEstimate.current = false

      if (estimateError) {
        prepareEstimate(_amountFrom, EExchangeDirections.direct, to, from)
      }
    } else {
      prepareEstimate(_amountFrom, EExchangeDirections.direct, to, from)
    }
  }, [from, to, _amountFrom, ltvPercent, activeAprOption, isExternalWallet])

  useEffect(() => {
    dispatch(fetchSettings())
    clearTimeout(timer.current)
    setIsExternalWallet(false)

    const isExternal = sessionStorage.getItem('loanCalculator:isExternal')
    if (isExternal) {
      setIsExternalWallet(+isExternal === 1)
    }

    if (typeof sessionStorage !== 'undefined') {
      const percent = sessionStorage.getItem('loanCalculator:percent')
      if (percent && !amountChange.current) {
        setAmountPercent(+percent)
      }
    }

    return () => {
      clearTimeout(timer.current)
    }
  }, [])

  useEffect(() => {
    dispatch(fetchWallets())
  }, [dispatch])

  useEffect(() => {
    if (isFirstVisited) {
      if (floorNumber(defaultFrom, 6) !== 0) {
        if (amountPercent !== 100) {
          setAmountFrom(floorNumber((defaultFrom * amountPercent) / 100, 6).toString())
        } else {
          setAmountFrom(floorNumber(defaultFrom, 6).toString())
        }
      } else {
        setAmountFrom(floorNumber(+from?.loan_deposit_default_amount, 6).toString() || '10000')
      }
    } else {
      setAmountFrom(floorNumber(defaultFrom, 6).toString())
    }

    if (!isExternalWallet) {
      const amount = (+defaultFrom * amountPercent) / 100

      if (floorNumber(defaultFrom, 6) !== 0) {
        setAmountFrom(countDecimalPlaces(amount) > 6 ? amount.toFixed(6).replace(/0+$/, '') : amount.toString())
      } else {
        setAmountFrom(floorNumber(+from?.loan_deposit_default_amount, 6).toString() || '10000')
      }
    } else {
      setAmountFrom(floorNumber(+from?.loan_deposit_default_amount, 6).toString() || '10000')
    }
  }, [defaultFrom])

  // for visible forms
  useEffect(() => {
    const { isValid } = validator.current.validate()
    if (!isValid) {
      setIsBtnActive(false)
    } else if (
      +amountFrom.current < +from?.loan_deposit_min_amount ||
      (!isExternalWallet && +amountFrom.current > defaultFrom)
    ) {
      setIsBtnActive(false)
    } else {
      setIsBtnActive(true)
    }
  }, [amountFrom.current, amountTo])

  useEffect(() => {
    if (isFirstChanged) {
      sessionStorage.setItem('loanCalculator:percent', amountPercent.toString())
    }
  }, [amountPercent])

  useEffect(() => {
    if (wallets.length && !isWalletsReceived) {
      setIsDefaultFromLoaded(true)
      const wallet = wallets.find(
        (wallet) =>
          wallet.code.toLowerCase() === from?.code.toLowerCase() &&
          wallet.network.toLowerCase() === from?.network.toLowerCase(),
      )

      if (wallet) {
        setDefaultFrom(wallet.balance ?? 0)
        dispatch(setActiveCodeFrom(from.code))
        setIsInitialMount(false)
      } else {
        setDefaultFrom(0)
        setIsInitialMount(false)
      }
      setIsWalletsReceived(true)
    }
  }, [wallets])

  useEffect(() => {
    if (!isExternalWallet && +_amountFrom > defaultFrom && isDefaultFromLoaded) {
      setisNotEnoughBalance(true)
      setIsBtnActive(false)
    } else {
      setisNotEnoughBalance(false)
      setIsBtnActive(true)
    }

    if (
      !isExternalWallet &&
      !filteredDepositCurrencies.some(
        (cur) =>
          cur.code.toLowerCase() === from.code.toLowerCase() &&
          cur.network.toLowerCase() === from.network.toLowerCase(),
      )
    ) {
      setFrom(filteredDepositCurrencies[0])
      setListDepositCurrencies(filteredDepositCurrencies)

      return
    }

    if (isExternalWallet) {
      const defaultAmount = from?.loan_deposit_default_amount || '10000'
      setDefaultFrom(+defaultAmount)
      setAmountFrom(defaultAmount)

      setListDepositCurrencies(
        allDepositCurrencies.filter(
          (cur) => cur.loan_deposit_max_amount === null || +cur.loan_deposit_max_amount !== 0,
        ),
      )
      setListLoanCurrencies(allLoanCurencies)
      dispatch(setActiveCodeFrom(from?.code))
    } else {
      const wallet = wallets.find(
        (wallet) =>
          wallet.code.toLowerCase() === from?.code.toLowerCase() &&
          wallet.network.toLowerCase() === from.network.toLowerCase(),
      )
      setListDepositCurrencies(
        filteredDepositCurrencies.filter(
          (cur) => cur.loan_deposit_max_amount === null || +cur.loan_deposit_max_amount !== 0,
        ),
      )

      setListLoanCurrencies(filteredLoanCurencies)
      if (wallet) {
        setDefaultFrom(wallet.balance ? floorNumber(wallet.balance, 6) : 0)
        setAmountFrom((wallet.balance ? floorNumber(wallet.balance, 6) : 0).toString())
        dispatch(setActiveCodeFrom(from.code))
      } else {
        setDefaultFrom(0)
      }
    }
  }, [isExternalWallet])

  useEffect(() => {
    const parseCurrencies = async () => {
      if (currencies?.length === 0) {
        return
      }

      skipToEstimate.current = true

      let depositCurrencies: ICurrencyWithBalanceAndRate[] = []
      let loanCurrencies: ICurrency[] = []
      let foundPageCurrency: ICurrency = null

      for (const currency of currencies) {
        if (currency.is_loan_deposit_enabled) {
          depositCurrencies.push(currency)
        }
        if (currency.is_loan_receive_enabled) {
          loanCurrencies.push(currency)
        }
        if (pageCurrency && !foundPageCurrency) {
          if (
            currency.code.toLowerCase() === pageCurrency.code.toLowerCase() &&
            currency.network.toLowerCase() === pageCurrency.network.toLowerCase()
          ) {
            foundPageCurrency = currency
          }
        }
      }

      depositCurrencies = depositCurrencies.sort((a, b) => {
        if (a.loan_deposit_priority === null) return 1
        if (b.loan_deposit_priority === null) return -1
        return a.loan_deposit_priority - b.loan_deposit_priority
      })
      depositCurrencies = mapCurrenciesAndWallets(wallets, depositCurrencies)

      loanCurrencies = loanCurrencies.sort((a, b) => {
        if (a.loan_receive_priority === null) return 1
        if (b.loan_receive_priority === null) return -1
        return a.loan_receive_priority - b.loan_receive_priority
      })
      loanCurrencies = mapCurrenciesAndWallets(wallets, loanCurrencies)

      let newFrom: ICurrency
      let newTo: ICurrency

      if (foundPageCurrency) {
        if (foundPageCurrency.is_loan_deposit_enabled) {
          newFrom = foundPageCurrency
        } else if (foundPageCurrency.is_loan_receive_enabled) {
          newTo = foundPageCurrency
          newFrom = depositCurrencies.find(filterCurrenciesList(newTo))
        }
      } else {
        newFrom = depositCurrencies[0]
      }

      if (!newTo) {
        newTo = loanCurrencies.find(filterCurrenciesList(newFrom))
      }

      if (!isFirstChanged) {
        const fromCur = getCurrency(
          initialFromCode || sessionStorage.getItem('loanCalculator:fromCurrencyCode'),
          initialFromNetwork || sessionStorage.getItem('loanCalculator:fromCurrencyNetwork'),
        )

        const toCur = getCurrency(
          initialToCode || sessionStorage.getItem('loanCalculator:toCurrencyCode'),
          initialToNetwork || sessionStorage.getItem('loanCalculator:toCurrencyNetwork'),
        )

        setFrom(fromCur ?? newFrom)
        setTo(toCur ?? newTo)

        if (isExternalWallet) {
          setListLoanCurrencies(loanCurrencies)
          setListDepositCurrencies(
            depositCurrencies.filter(
              (cur) => cur.loan_deposit_max_amount === null || +cur.loan_deposit_max_amount !== 0,
            ),
          )
        } else {
          setListLoanCurrencies(
            loanCurrencies.filter((cur) => cur.is_allowed_for_wallet && cur.is_loan_deposit_enabled),
          )
          setListDepositCurrencies(
            depositCurrencies.filter(
              (cur) =>
                cur.is_allowed_for_wallet &&
                cur.is_loan_deposit_enabled &&
                (cur.loan_deposit_max_amount === null || +cur.loan_deposit_max_amount !== 0),
            ),
          )
        }

        setAllDepositCurrencies(depositCurrencies)
        setAllLoanCurrencies(loanCurrencies)

        setFilteredDepositCurrencies(
          depositCurrencies.filter(
            (cur) =>
              cur.is_allowed_for_wallet &&
              cur.is_loan_deposit_enabled &&
              (cur.loan_deposit_max_amount === null || +cur.loan_deposit_max_amount !== 0),
          ),
        )

        setFilteredLoanCurrencies(
          loanCurrencies.filter((cur) => cur.is_allowed_for_wallet && cur.is_loan_deposit_enabled),
        )
      } else {
        if (isMounted.current) {
          setFrom(newFrom)
          setTo(newTo)

          if (isExternalWallet) {
            setListLoanCurrencies(loanCurrencies)
            setListDepositCurrencies(
              depositCurrencies.filter(
                (cur) => cur.loan_deposit_max_amount === null || +cur.loan_deposit_max_amount !== 0,
              ),
            )
          } else {
            setListLoanCurrencies(
              loanCurrencies.filter((cur) => cur.is_allowed_for_wallet && cur.is_loan_deposit_enabled),
            )
            setListDepositCurrencies(
              depositCurrencies.filter(
                (cur) => cur.is_allowed_for_wallet && cur.is_loan_deposit_enabled && +cur.loan_deposit_max_amount !== 0,
              ),
            )
          }

          setAllDepositCurrencies(depositCurrencies)
          setAllLoanCurrencies(loanCurrencies)

          setFilteredDepositCurrencies(
            depositCurrencies.filter((cur) => cur.is_allowed_for_wallet && cur.is_loan_deposit_enabled),
          )

          setFilteredLoanCurrencies(
            loanCurrencies.filter((cur) => cur.is_allowed_for_wallet && cur.is_loan_deposit_enabled),
          )
        }

        applyInitialCurrencies()
      }
    }

    if (currencies.length && wallets.length && !isCurrenciesFetched && isCurrenciesUpdated) {
      void parseCurrencies()
      setIsCurrenciesFetched(true)
    }
  }, [pageCurrency, currencies, wallets])

  useEffect(() => {
    applyInitialCurrencies()
  }, [router.isReady])

  useEffect(() => {
    if (!to) {
      return
    }

    if (skipToEstimate.current) {
      skipToEstimate.current = false
    } else {
      if (!Number.isNaN(Number(amountFrom.current))) {
        prepareEstimate(amountFrom.current, EExchangeDirections.direct)
      }
    }
  }, [to])

  useEffect(() => {
    if (!Number.isNaN(Number(amountFrom.current)) && isFirstVisited) {
      prepareEstimate(amountFrom.current, EExchangeDirections.direct)
    }
  }, [amountFrom.current])

  useEffect(() => {
    if (wallets.length && isWalletsReceived && to) {
      const wallet = wallets.find(
        (item) => item.code === to.code.toLowerCase() && item.network === to.network.toLowerCase(),
      )

      if (wallet) {
        setWalletToBalance(wallet.balance)
      } else {
        setWalletToBalance(0)
      }
    }
  }, [to, isWalletsReceived])

  useEffect(() => {
    if (!from || !wallets.length) {
      return
    }

    isAprOptionsSet.current = false

    let newTo = null
    if (from.code === to?.code && from.network === to?.network) {
      skipToEstimate.current = true
      newTo = listLoanCurrencies.find(filterCurrenciesList(from))
      setTo(newTo)
    } else if (from.is_stable && to?.is_stable) {
      skipToEstimate.current = true
      newTo = listLoanCurrencies.find((el) => el.is_stable)
      setTo(newTo)
    }

    if (isExternalWallet) {
      const defaultAmount = from?.loan_deposit_default_amount || '10000'
      setDefaultFrom(+floorNumber(+defaultAmount, 6))
      setAmountFrom(floorNumber(+defaultAmount / 2, 6).toString())
      dispatch(setActiveCodeFrom(from.code))
    } else {
      const wallet = wallets.find(
        (wallet) =>
          wallet.code.toLowerCase() === from.code.toLowerCase() &&
          wallet.network.toLowerCase() === from.network.toLowerCase(),
      )

      if (wallet) {
        setDefaultFrom(wallet.balance ? floorNumber(wallet.balance, 6) : 0)
        const percent = sessionStorage.getItem('loanCalculator:percent')
        if (percent && !amountChange.current) {
          setAmountPercent(+percent)
        } else {
          setAmountPercent(100)
        }
        if (amountPercent !== 100) {
          setAmountFrom(floorNumber((wallet.balance * amountPercent) / 100, 6).toString())
        } else {
          setAmountFrom(floorNumber(wallet.balance, 6).toString())
        }

        if (floorNumber(wallet.balance, 6) === 0) {
          const percent = sessionStorage.getItem('loanCalculator:percent')
          if (percent && !amountChange.current) {
            setAmountPercent(+percent)
          } else {
            setAmountPercent(0)
          }
          setAmountFrom(floorNumber(+from?.loan_deposit_default_amount, 6).toString())
        }
        dispatch(setActiveCodeFrom(from.code))
      } else {
        setDefaultFrom(0)
        setAmountFrom(floorNumber(+from?.loan_deposit_default_amount, 6).toString())
        const percent = sessionStorage.getItem('loanCalculator:percent')
        if (percent && !amountChange.current) {
          setAmountPercent(+percent)
        } else {
          setAmountPercent(0)
        }
      }
    }
  }, [from, isWalletsReceived])

  useEffect(() => {
    if (from && defaultFrom === 0) {
      setisNotEnoughBalance(true)
      setIsBtnActive(false)
    } else {
      setisNotEnoughBalance(false)
      setIsBtnActive(true)
    }
  }, [from, defaultFrom])

  useEffect(() => {
    setLoadingTo(true)

    const newLtvOptions = (from?.ltv_options ?? settings?.ltvOptions ?? []).map((it) => `${+it * 100}%`)
    const activeTab = from?.ltv_options?.[from?.ltv_options?.length - 1] ?? settings?.ltvDefaultOption ?? '0'

    setLvtOptions(newLtvOptions)
    setLtvPercent(activeTab)
  }, [from, settings?.ltvDefaultOption])

  useEffect(() => {
    setLoadingTo(true)
  }, [ltvPercent, activeAprOption])

  useEffect(() => {
    if (isLiquidityLoaded) {
      if (maxLoanAmountWithoutLtv) {
        setMaxLoanAmount(maxLoanAmountWithoutLtv)
      } else {
        setMaxLoanAmount(
          new BigNumber(maxDepositAmountWithoutLtv)
            .multipliedBy(ltvPercent)
            .decimalPlaces(6, BigNumber.ROUND_HALF_DOWN)
            .toString(),
        )
      }
    }
  }, [ltvPercent, maxLoanAmountWithoutLtv, maxDepositAmountWithoutLtv])

  const filterCurrenciesList = (exclude: ICurrency) => (item: ICurrency) => {
    return item.code !== exclude?.code && item.network !== exclude?.network
  }

  const estimate = (
    amount: string,
    direction: EExchangeDirections,
    toCurrency: ICurrency,
    fromCurrency: ICurrency,
  ) => async () => {
    requestedPairRef.current = [fromCurrency, toCurrency]

    if (isFirstChanged) {
      sessionStorage.setItem('loanCalculator:isExternal', isExternalWallet ? '1' : '0')
    }

    const now = new Date()
    const aprOption = aprOptions?.find((it) => it?.id === activeAprOption)

    const { response } = await estimateLoanAmount({
      from_code: fromCurrency.code,
      from_network: fromCurrency.network,
      to_code: toCurrency.code,
      to_network: toCurrency.network,
      amount: amount,
      exchange: direction,
      ltv_percent: ltvPercent,
      receive_to: 'internal_wallet',
      receive_from: isExternalWallet ? 'external_wallet' : 'internal_wallet',
      expected_close_date:
        aprOption?.id !== 'short_term' ? null : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      date_from: now.toISOString(),
      is_dynamic: aprOption?.isDynamic?.toString(),
      round_direction: estimateRoundDirection.current,
    })

    if ('message' in response) {
      setEstimateError(response.message)
      setAmountTo('—')
    } else {
      setEstimateError('')
      if (direction === EExchangeDirections.direct) setAmountTo(toFixed(response.amount_to, toCurrency?.decimal_places))
      else handleSetAmountFrom(response.amount_from)

      const [fromRef, toRef] = requestedPairRef.current
      if (
        fromRef.code === fromCurrency.code &&
        fromRef.network === fromCurrency.network &&
        toRef.code === toCurrency.code &&
        toRef.network === toCurrency.network
      ) {
        setExchangeData(response)
      }

      const options = buildAprOptions(response, t, user?.is_dynamic_loans_available)
      setAprOptions(options)

      if (!isAprOptionsSet.current) {
        setActiveAprOption(options?.find((op) => op?.id === activeAprOption)?.id ?? 'popular')
        isAprOptionsSet.current = true
      }
    }

    setLoadingTo(false)
    setLoadingFrom(false)
    setIsFirstVisited(false)
  }

  const prepareEstimate = (amount: any, direction: EExchangeDirections, _to?: ICurrency, _from?: ICurrency) => {
    clearTimeout(timer.current)

    const toCurrency = _to || to
    const fromCurrency = _from || from

    if (!toCurrency || !fromCurrency || Number.isNaN(parseFloat(amount))) {
      return
    }

    if (direction === EExchangeDirections.direct) setLoadingTo(true)
    else setLoadingFrom(true)

    timer.current = setTimeout(estimate(amount, direction, toCurrency, fromCurrency), 200)
  }

  const handleChangeLtvPercent = (value: string) => {
    const numeric = parseFloat(value.replace('%', '')) / 100
    setLtvPercent(String(numeric))
  }

  async function handleCreateLoan() {
    const { isValid } = validator.current.validate()

    if (user?.loans_disabled) {
      setIsKYCPopupActive(true)
      return
    }

    try {
      await fetchLiquitity()
    } catch (error) {}

    ga.event({
      action: 'Loan_account_step_1_get',
    })

    if (!isValid) {
      return
    }

    setIsLoading(true)

    const aprOption = aprOptions?.find((it) => it?.id === activeAprOption)

    const loan = await createLoan({
      user_id: user?.id,
      deposit: {
        currency_code: from.code,
        currency_network: from.network,
        expected_amount: exchangeData.amount_from,
        receive_from: isExternalWallet ? 'external_wallet' : 'internal_wallet',
      },
      loan: {
        currency_code: to.code,
        currency_network: to.network,
        receive_to: 'internal_wallet',
      },
      ltv_percent: ltvPercent,
      expected_close_date:
        aprOption?.id !== 'short_term' ? null : new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      is_dynamic: aprOption?.isDynamic,
    })

    if (!loan || (typeof loan !== 'string' && !loan.loan_id)) {
      setIsLoading(false)
      return
    }

    if (typeof loan === 'string' && loan === 'KYC') {
      setIsKYCPopupActive(true)
      setIsLoading(false)
    }

    if (typeof loan === 'string' && loan?.toLowerCase()?.includes('dynamic loan')) {
      setCreateError(
        loan?.includes('unavailable')
          ? t('dynamic_loan_unavailable')
          : locale === 'en'
          ? loan
          : t('dynamic_loan_amount_exceeds_limit'),
      )
      setIsLoading(false)

      setTimeout(() => setCreateError(''), 10000)
    }

    if (typeof loan !== 'string') {
      await router.push({
        pathname: '/account/loans/confirm',
        query: {
          from_code: from.code,
          from_network: from.network,
          to_code: to.code,
          to_network: to.network,
          amount: exchangeData.amount_from,
          loan_id: loan.loan_id,
          ltv_percent: ltvPercent,
          isExternal: isExternalWallet,
          duration: aprOption?.period,
          is_dynamic: aprOption?.isDynamic,
        },
      })
    }
  }

  const handleSetAmountFrom = (amount: string) => {
    clearTimeout(timer.current)
    sessionStorage.setItem('loanCalculator:amount', amount)
    amountChange.current = true

    if (!isExternalWallet && +amount > defaultFrom) {
      setisNotEnoughBalance(true)
      setIsBtnActive(false)
    } else if (+amount < +from.loan_deposit_min_amount) {
      setisNotEnoughBalance(false)
      setIsBtnActive(false)
    } else {
      setisNotEnoughBalance(false)
      setIsBtnActive(true)
    }

    if (!isInitialMount && floorNumber(defaultFrom, 6) !== 0) {
      if (floorNumber(defaultFrom, 6) === 0) {
        setAmountPercent(0)
        sessionStorage.setItem('loanCalculator:percent', '0')
      }
      if (+amount >= defaultFrom) {
        setAmountPercent(100)
        sessionStorage.setItem('loanCalculator:percent', '100')
      } else {
        const percent = (+amount / defaultFrom) * 100
        setAmountPercent(Math.round(percent))
        sessionStorage.setItem('loanCalculator:percent', Math.round(percent).toString())
      }
    }

    setAmountFrom(amount)
    setIsFirstChanged(true)
  }

  const handleSetAmountTo = (amount: string) => {
    clearTimeout(timer.current)
    setAmountTo(amount)

    prepareEstimate(amount, EExchangeDirections.reverse)
    skipToEstimate.current = true
    prepareEstimate(amount, EExchangeDirections.reverse)

    setIsLiquidityLoaded(true)
  }

  const changePercent = (percent: number) => {
    amountChange.current = true
    sessionStorage.setItem(
      'loanCalculator:amount',
      roundNumberWithPrecision((+defaultFrom * percent) / 100, 6).toString(),
    )
    sessionStorage.setItem('loanCalculator:percent', percent.toString())
    setAmountFrom(roundNumberWithPrecision((+defaultFrom * percent) / 100, 6).toString())
    setAmountPercent(percent)
    setisNotEnoughBalance(false)
    setIsFirstChanged(true)
  }

  const notEnoughBalanceClick = () => {
    dispatch(
      setActiveWallet(
        wallets &&
          wallets.find(
            (el) =>
              el.code?.toLowerCase() === from.code?.toLowerCase() &&
              el.network?.toLowerCase() === from.network?.toLowerCase(),
          ),
      ),
    )

    if (
      wallets &&
      wallets.find(
        (el) =>
          el.code?.toLowerCase() === from.code?.toLowerCase() &&
          el.network?.toLowerCase() === from.network?.toLowerCase(),
      )
    ) {
      router.push(`/account/wallets/info/?code=${from.code}&network=${from.network}`)
    } else {
      router.push('/account/wallets')
    }
  }

  useEffect(() => {
    let maxAmount = '0'
    if (to?.loan_receive_max_amount) {
      if (from?.loan_deposit_max_amount) {
        maxAmount = safeMin(
          to?.loan_receive_max_amount,
          new BigNumber(from?.loan_deposit_max_amount).multipliedBy(exchangeData?.rate).multipliedBy(ltvPercent),
          new BigNumber(maxLoanAmount),
        )
          ?.decimalPlaces(6, BigNumber.ROUND_DOWN)
          ?.toString()
      } else {
        maxAmount = safeMin(to?.loan_receive_max_amount, new BigNumber(maxLoanAmount))
          ?.decimalPlaces(6, BigNumber.ROUND_DOWN)
          ?.toString()
      }
    } else if (from?.loan_deposit_max_amount) {
      if (to?.loan_receive_max_amount) {
        maxAmount = safeMin(
          to?.loan_receive_max_amount,
          new BigNumber(from?.loan_deposit_max_amount).multipliedBy(exchangeData?.rate).multipliedBy(ltvPercent),
          new BigNumber(maxLoanAmount),
        )
          ?.decimalPlaces(6, BigNumber.ROUND_DOWN)
          ?.toString()
      } else {
        maxAmount = safeMin(
          new BigNumber(from?.loan_deposit_max_amount).multipliedBy(exchangeData?.rate).multipliedBy(ltvPercent),
          new BigNumber(maxLoanAmount),
        )
          ?.decimalPlaces(6, BigNumber.ROUND_DOWN)
          ?.toString()
      }
    } else {
      maxAmount = new BigNumber(maxLoanAmount).toString()
    }
    setActualMaxAmount(maxAmount)

    const min = from?.loan_deposit_min_amount
      ? safeMax(
          new BigNumber(to?.loan_receive_min_amount),
          new BigNumber(from?.loan_deposit_min_amount).multipliedBy(exchangeData?.rate).multipliedBy(ltvPercent),
        )
          .decimalPlaces(6, BigNumber.ROUND_UP)
          .toString()
      : new BigNumber(from?.loan_receive_min_amount).decimalPlaces(6, BigNumber.ROUND_UP).toString()
    setActualMinAmount(min)
  }, [exchangeData?.rate, maxLoanAmount, to, from])

  const renderLimits = () => {
    if (!estimateError && activeAprOption === 'min_rate') {
      return (
        !isLoadingTo &&
        exchangeData?.dynamic_loans_max_limit_usd && (
          <>
            {+exchangeData?.dynamic_loans_max_limit_usd <= 0 ? (
              <span className={cn(styles.errorAmount, styles.errorAmount__danger)}>
                {t('dynamic_loan_unavailable')}
              </span>
            ) : (
              <span
                className={cn(
                  styles.errorAmount,
                  +exchangeData?.dynamic_loans_max_limit_usd < +amountTo
                    ? styles.errorAmount__danger
                    : styles.dynamic__limit,
                )}
              >
                {replacePlaceholders(t('limit_for_dynamic_loans'), {
                  amount: floorBigNumber(exchangeData?.dynamic_loans_max_limit_usd, 6),
                })}
              </span>
            )}
          </>
        )
      )
    }

    if (
      (!isNaN(+actualMinAmount) && +amountTo < +actualMinAmount && isLiquidityLoaded && !isLoadingTo) ||
      (estimateError === 'min_amount_is' && !isNaN(+actualMinAmount))
    ) {
      return (
        <div className={cn(styles.limits, styles.limits__error)}>
          {t('min_is')}{' '}
          <span
            onClick={() => {
              estimateRoundDirection.current = 'up'
              handleSetAmountTo(actualMinAmount)
              skipToEstimate.current = true
            }}
          >
            {actualMinAmount}
          </span>{' '}
          {to?.code?.toUpperCase()}.
        </div>
      )
    }
    if (+amountTo > +actualMaxAmount && isLiquidityLoaded && !isLoadingTo && !isNaN(+actualMaxAmount)) {
      return (
        <div className={cn(styles.limits, styles.limits__error)}>
          {t('max_is')}{' '}
          <span
            onClick={() => {
              estimateRoundDirection.current = 'down'
              handleSetAmountTo(actualMaxAmount)
              skipToEstimate.current = true
            }}
          >
            {actualMaxAmount}
          </span>{' '}
          {to?.code?.toUpperCase()}.{' '}
          {replacePlaceholdersWithElements(t('you_can_contact_support'), {
            contactSupport: <span onClick={goToChat}>{t('contact_support')}</span>,
          })}
        </div>
      )
    }

    return (isLiquidityLoaded &&
      maxLoanAmount &&
      amountTo &&
      !isLoadingTo &&
      (!isNaN(+actualMaxAmount) || !isNaN(+actualMinAmount))) ||
      (isLiquidityError && (!isNaN(+actualMaxAmount) || !isNaN(+actualMinAmount)) && isLiquidityLoaded) ? (
      <div className={cn(styles.limits)}>
        <div>
          {t('availabe_liquidity_amount')}:{' '}
          {actualMaxAmount !== undefined && actualMaxAmount !== null && !isNaN(Number(actualMaxAmount)) ? (
            <></>
          ) : (
            t('from') + ' '
          )}
          {actualMinAmount !== null && actualMinAmount !== undefined && !isNaN(Number(actualMinAmount)) ? (
            <></>
          ) : (
            t('up_to') + ' '
          )}
          {actualMinAmount !== null && actualMinAmount !== undefined && !isNaN(Number(actualMinAmount)) && (
            <>
              <span
                onClick={() => {
                  estimateRoundDirection.current = 'up'
                  setIsLiquidityLoaded(false)
                  handleSetAmountTo(actualMinAmount)
                  skipToEstimate.current = true
                }}
              >
                {addSpacesInNumber(actualMinAmount)}
              </span>{' '}
            </>
          )}
          {actualMaxAmount !== undefined && actualMaxAmount !== null && !isNaN(Number(actualMaxAmount)) && (
            <>
              {actualMinAmount !== null && actualMinAmount !== undefined && !isNaN(Number(actualMinAmount)) ? '- ' : ''}
              <span
                onClick={() => {
                  estimateRoundDirection.current = 'down'
                  setIsLiquidityLoaded(false)
                  handleSetAmountTo(actualMaxAmount)
                  skipToEstimate.current = true
                }}
              >
                {addSpacesInNumber(actualMaxAmount)}
              </span>
            </>
          )}{' '}
          {to?.code?.toUpperCase()}.
        </div>
      </div>
    ) : isLiquidityError ? (
      <></>
    ) : (
      <Skeleton className={styles.liquidityLoader} />
    )
  }

  return (
    <ValidatorWrapper ref={validator}>
      <div className={styles.calculator}>
        {user && (
          <button className={styles.back} onClick={() => router.push('/account/loans')}>
            <img src={BackIcon} alt="back icon" />
          </button>
        )}
        <ContentHeader title={t('get_instant_loan')} className={styles.header} />

        <div className={styles.form}>
          <ValidatorField value={amountFrom.current} rules={validatorRules.depositAmount(from, t, replacePlaceholders)}>
            {({ isValid, message }) => (
              <InputCalculator
                isError={
                  (!isValid && !isLoadingFrom && (amountTo !== '—' || amountFrom.current !== '—')) || isNotEnoughBalance
                }
                className={styles.inputFrom}
                onEnter={handleCreateLoan}
                coin={from}
                isLoading={isLoadingFrom || isNaN(Number(_amountFrom))}
                onChangeAmount={handleSetAmountFrom}
                onChangeCoin={handleSetFrom}
                value={_amountFrom}
                label={t('collateral_capital')}
                list={listDepositCurrencies.sort((a, b) => {
                  const aBalance = +a.balance
                  const bBalance = +b.balance
                  const aRate = isNaN(+a.rate) ? 0 : +a.rate
                  const bRate = isNaN(+b.rate) ? 0 : +b.rate

                  // Сначала сравниваем по произведению balance * rate
                  const aValue = aBalance * aRate
                  const bValue = bBalance * bRate

                  if (aValue < bValue) return 1
                  if (aValue > bValue) return -1

                  // Если произведения равны, оставляем в исходном порядке
                  return 0
                })}
                isExternalWallet={isExternalWallet}
                onWalletSwitch={onWalletSwitch}
                hasSwitchWalletBtn
                balance={defaultFrom}
                inputMode="decimal"
              >
                {!isValid && !isLoadingFrom && (amountTo !== '—' || amountFrom.current !== '—') && (
                  <div className={cn(styles.errorAmount, { [styles.errorAmount_internal]: isExternalWallet })}>
                    {t(message)}
                  </div>
                )}
                {isNotEnoughBalance &&
                  !estimateError &&
                  !(!isValid && !isLoadingFrom && (amountTo !== '—' || amountFrom.current !== '—')) && (
                    <div className={cn(styles.errorAmount, styles.errorAmountMobile)}>
                      {t('not_enough_balance_for_loan')}.{' '}
                      <button className={styles.invisible} onClick={notEnoughBalanceClick}>
                        <a>{replacePlaceholders(t('top_up_wallet'), { code: from.code })} </a>
                      </button>
                    </div>
                  )}
              </InputCalculator>
            )}
          </ValidatorField>

          {!isExternalWallet ? (
            <PercentSlider
              amountPercent={amountPercent}
              defaultFrom={defaultFrom}
              percentCoefficient={percentCoefficient}
              changePercent={changePercent}
              className={styles.percentSlider}
            />
          ) : (
            <></>
          )}

          <ValidatorField value={amountTo}>
            {() => (
              <InputCalculator
                onEnter={handleCreateLoan}
                coin={to}
                isLoading={isLoadingTo}
                onChangeAmount={handleSetAmountTo}
                onChangeCoin={handleSetTo}
                value={amountTo}
                label={t('loan')}
                list={listLoanCurrencies.sort((a, b) => {
                  const aBalance = +a.balance
                  const bBalance = +b.balance
                  const aRate = isNaN(+a.rate) ? 0 : +a.rate
                  const bRate = isNaN(+b.rate) ? 0 : +b.rate

                  // Сначала сравниваем по произведению balance * rate
                  const aValue = aBalance * aRate
                  const bValue = bBalance * bRate

                  if (aValue < bValue) return 1
                  if (aValue > bValue) return -1

                  // Если произведения равны, оставляем в исходном порядке
                  return 0
                })}
                isStableFirst={true}
                balance={floorNumber(walletToBalance, 6)}
                inputMode="decimal"
              >
                {renderLimits()}
              </InputCalculator>
            )}
          </ValidatorField>
        </div>

        <div>
          <TermsBlock
            exchange={exchangeData}
            to={to}
            ltvOptions={ltvOptions}
            handleChangeLvtPercent={handleChangeLtvPercent}
            ltvPercent={+ltvPercent * 100 + '%'}
            handleChangeAprOption={handleChangeAprOption}
            aprOptions={aprOptions}
            activeAprOption={activeAprOption}
          />

          <ButtonBlue
            disableHover={true}
            disabled={
              !isBtnActive ||
              !(+amountTo >= +to?.loan_receive_min_amount && +amountTo <= +actualMaxAmount) ||
              (+exchangeData?.dynamic_loans_max_limit_usd <= +amountTo && activeAprOption === 'min_rate')
            }
            isLoading={isLoading || !isLiquidityLoaded}
            onClick={handleCreateLoan}
            className={styles.button}
          >
            {t('get_loan')}

            {createError && <span className={styles.confirm__error}>{createError}</span>}
          </ButtonBlue>
        </div>

        {isKYCPopupActive && (
          <KYCPopup
            setIsOpened={setIsKYCPopupActive}
            subtitle={''}
            buttonText={t('kyc_verification')}
            onClick={verifyKYC}
            text={t('loan_limit_exceeded_kyc_text')}
          />
        )}
      </div>
    </ValidatorWrapper>
  )
}

export default Calculator
